import React, { createContext, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { LocalStorage } from "./LocalStorage";
import { apiBaseUrl, get, post } from "../Services/api.service";
import { useNotification } from "../Components/CustomStatus/UseNotification";
import { Paths } from "../Services/EndPoints";
import { isEmpty } from "lodash";
import { useDispatch } from "react-redux";
import { clearLoginPrescribeData } from "../Redux/Actions";
const AuthContext = createContext({
  user: {
    token: "",
  },
  login(data) {},
  logout() {},
  isLoading: false,
});

export const AuthProvider = ({ children }) => {
  const { t } = useTranslation();
  const [user, setUser] = LocalStorage("userData", null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { openToast } = useNotification();

  const generateSessionToken = () => {
    const timestamp = Date.now();
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let token = "";
    for (let i = 0; i < 10; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      token += characters[randomIndex];
    }
    return `${timestamp}${token}`;
  };

  const login = async (
    mobile,
    otp,
    setOtp,
    state,
    setSpin,
    status,
    prescribeDetails,
    requestId,
  ) => {
    setIsLoading(true);
    setSpin(true);
    return post(
      `${apiBaseUrl}${Paths.verify_otp}?mobileNumber=${encodeURIComponent(
        mobile,
      )}&otp=${encodeURIComponent(otp)}`,
      // `${apiBaseUrl}${Paths.verify_otp}?mobileNumber=${encodeURIComponent(
      //   mobile
      // )}&otp=${encodeURIComponent(otp)}&requestId=${requestId}`
    )
      .then((res) => {
        if (!isEmpty(res.data)) {
          setUser({
            token: res?.data?.jwt,
            number: res?.data?.mobileNumber,
            userId: res?.data?.userId,
          });
          sessionStorage.removeItem("sessionToken");
          window.localStorage.setItem("name", res?.data?.fullName || null);
          if (status === "true") {
            console.log(444);
            get(Paths?.prc_to_cart + `/${prescribeDetails?.number}`)
              .then((res) => {
                if (res?.statusCode === 204) {
                  openToast({
                    content: res.data?.message || t("auth.prescriptionInCart"),
                    type: "success",
                  });
                }
                dispatch(clearLoginPrescribeData());
                navigate(state?.from);
              })
              .catch((err) => console.log(err));
          } else {
            if (state && state?.from !== null && state?.from !== undefined) {
              navigate(state?.from);
            } else {
              navigate("/");
            }
          }
        }
        openToast({
          content: res.data?.message || t("auth.loggedIn"),
          type: "success",
        });
        setOtp("");
        return res;
      })
      .catch((err) => {
        openToast({
          content: err?.response?.data?.message || "check the otp",
          type: "error",
        });
        throw err;
      })
      .finally(() => {
        setSpin(false);
        setIsLoading(false);
      });
  };
  // Non-navigating login for the checkout LoginSheet. Same verify_otp call as
  // `login`, but no navigate / prescribe / cart side effects — the caller (a
  // bottom sheet) stays put; setting `user` re-runs useAddresses/useCart.
  const loginWithOtp = async (mobile, otp, dialCode) => {
    // Bare number + separate dialCode (omit for India → backend defaults "91",
    // so existing India verify calls are byte-identical).
    const dc = dialCode && dialCode !== "91" ? `&dialCode=${dialCode}` : "";
    return post(
      `${apiBaseUrl}${Paths.verify_otp}?mobileNumber=${encodeURIComponent(
        mobile,
      )}&otp=${encodeURIComponent(otp)}${dc}`,
    )
      .then((res) => {
        if (!isEmpty(res.data)) {
          setUser({
            token: res?.data?.jwt,
            number: res?.data?.mobileNumber,
            userId: res?.data?.userId,
          });
          sessionStorage.removeItem("sessionToken");
          window.localStorage.setItem("name", res?.data?.fullName || null);
          openToast({
            content: res.data?.message || "Successfully logged in",
            type: "success",
          });
        }
        return {
          raw: res.data,
          isReturning: !!res.data?.fullName,
          name: res.data?.fullName || "",
        };
      })
      .catch((err) => {
        throw err;
      });
  };

  // ── Phone + password (non-navigating, like loginWithOtp) ──
  // Errors are rethrown untouched: callers branch on err.response.data.statusCode
  // (see Utils/passwordAuth.js) and show err.response.data.message.
  const passwordBody = (mobile, dialCode, extra = {}) => ({
    mobileNumber: mobile,
    dialCode: dialCode || "91",
    lang: localStorage.getItem("i18nextLng") || "en",
    ...extra,
  });

  const applyLogin = (res) => {
    if (!isEmpty(res.data)) {
      setUser({
        token: res?.data?.jwt,
        number: res?.data?.mobileNumber,
        userId: res?.data?.userId,
      });
      sessionStorage.removeItem("sessionToken");
      // A new password account has no name until its first address is saved. localStorage only
      // stores text, so saving null wrote the word "null" and the menu showed it; with nothing
      // saved the menu falls back to "Welcome".
      if (res?.data?.fullName) {
        window.localStorage.setItem("name", res.data.fullName);
      } else {
        window.localStorage.removeItem("name");
      }
      openToast({ content: t("auth.loggedIn"), type: "success" });
    }
    return {
      raw: res.data,
      isReturning: !!res.data?.fullName,
      name: res.data?.fullName || "",
    };
  };

  const loginWithPassword = (mobile, password, dialCode) =>
    post(Paths.login_password, passwordBody(mobile, dialCode, { password })).then(applyLogin);

  const signupWithPassword = (mobile, password, dialCode) =>
    post(Paths.signup_password, passwordBody(mobile, dialCode, { password })).then(applyLogin);

  // WhatsApp code for "set a password" / "forgot password". Does not log in.
  const sendPasswordCode = (mobile, dialCode) =>
    post(Paths.password_otp, passwordBody(mobile, dialCode));

  const setPassword = (mobile, otp, password, dialCode) =>
    post(Paths.set_password, passwordBody(mobile, dialCode, { otp, password })).then(applyLogin);

  const logout = () => {
    setUser(null);
    localStorage.removeItem("userData");
    const sessionToken = generateSessionToken();
    sessionStorage.setItem("sessionToken", sessionToken);
    navigate("/", { replace: true });
    window.location.reload();
  };

  const value = useMemo(
    () => ({
      user,
      login,
      loginWithOtp,
      loginWithPassword,
      signupWithPassword,
      sendPasswordCode,
      setPassword,
      logout,
      isLoading,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};
